'use client'

import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { DictionaryContext, NotificationContext } from '..'
import { SignalRNotificationKey } from './SignalRNotificationKey'
import { SignalRNotificationTypes, SignalRTypes } from './signalRConstants'

const MESSAGE_KEYS = { SIGNAL_R: 'STO.ERROR.SIGNAL_R' }

export type SignalRMessageProps = {
    variant: SignalRNotificationTypes
    keyType: SignalRNotificationKey
    message: string
    title: string
    objectIdentifier?: string
}

interface SignalRResponseModel {
    data: SignalRData
}

interface SignalRData {
    /** This is the "variant" of the notification, i.e. success or warning */
    infoType: SignalRNotificationTypes
    keyType: SignalRNotificationKey
    message: string
    title: string
    objectIdentifier?: string
    type: SignalRTypes
}

/**
 * @property signalRNotifications - All current notifications
 * @property removeNotifications - Function to remove a number of notifications
 */
export type SignalRProviderValue = {
    signalRNotifications: SignalRMessageProps[]
    removeNotifications: (amount: number) => void
}

export const SignalRContext = createContext<SignalRProviderValue>({
    signalRNotifications: [],
    removeNotifications: (amount: number) => {},
})

/**
 * This provider is responsible for listening to Signal R notifications via websocket and storing the notifications.
 * Is not ready to be used in Reporting or any applications that use the same Next version or above.
 */
export const SignalRProvider = ({
    children,
    signalRClientURL,
    onConnect,
    onMessageReceived,
    onError,
}: {
    children: ReactNode
    signalRClientURL: string | undefined
    onConnect?: () => void
    onMessageReceived?: (parsedMessage: SignalRResponseModel) => void
    onError?: (error: any) => void
}): JSX.Element => {
    const notifications = useContext(NotificationContext)
    const [signalRNotifications, setSignalRNotifications] = useState<SignalRMessageProps[]>([])
    const [connection, setConnection] = useState<null | HubConnection>(null)
    const { getText } = useContext(DictionaryContext)

    useEffect(() => {
        if (signalRClientURL) {
            const signalRClientConnection = new HubConnectionBuilder()
                .withUrl(signalRClientURL, {
                    withCredentials: true,
                    skipNegotiation: false,
                    transport: HttpTransportType.WebSockets,
                })
                .withAutomaticReconnect()
                .build()
            setConnection(signalRClientConnection)
        }
    }, [signalRClientURL])

    useEffect(() => {
        if (!!connection && ![HubConnectionState.Connected, HubConnectionState.Connecting].includes(connection.state)) {
            connection
                .start()
                .then(() => {
                    if (onConnect) onConnect()
                    connection.on('SendAsync', (_, message: string) => {
                        if (!!!message) return
                        const parsedMessage = JSON.parse(message) as SignalRResponseModel
                        if (onMessageReceived) onMessageReceived(parsedMessage)

                        const { data } = parsedMessage
                        if (!!data) {
                            setSignalRNotifications((currentNotifications) => [
                                ...currentNotifications,
                                { ...data, variant: data.infoType },
                            ])
                            setNotification(data)
                        }
                    })
                })
                .catch((error) => {
                    if (onError) onError(error)
                    if (!signalRNotifications.some((notification) => notification.title === 'SignalR Error'))
                        setSignalRNotifications([
                            ...signalRNotifications,
                            {
                                variant: SignalRNotificationTypes.ERROR,
                                keyType: SignalRNotificationKey.None,
                                title: 'SignalR Error',
                                message: getText(MESSAGE_KEYS.SIGNAL_R),
                            },
                        ])
                })
        }
    }, [connection])

    const removeNotifications = (amount: number) => {
        if (signalRNotifications.length >= amount) {
            let newList = [...signalRNotifications]
            newList.splice(0, amount)
            setSignalRNotifications(newList)
        } else {
            setSignalRNotifications([])
        }
    }

    const setNotification = (signalRData: SignalRData) => {
        // When the signalRData is a response, we don't want to show a notification
        if (signalRData.type === SignalRTypes.RESPONSE) return

        switch (signalRData.infoType) {
            case SignalRNotificationTypes.SUCCESS:
                notifications.success({ title: signalRData.title, message: signalRData.message })
                break
            case SignalRNotificationTypes.WARNING:
                notifications.warning({ title: signalRData.title, message: signalRData.message })
                break
            case SignalRNotificationTypes.ERROR:
                notifications.error({ title: signalRData.title, message: signalRData.message })
                break
            case SignalRNotificationTypes.INFO:
            default:
                notifications.info({ title: signalRData.title, message: signalRData.message })
        }
    }

    const providerValue = useMemo(
        () => ({
            signalRNotifications,
            removeNotifications,
        }),
        [signalRNotifications],
    )

    return <SignalRContext.Provider value={providerValue}>{children}</SignalRContext.Provider>
}
